import { isInternalUser, isYardDriverUser } from '@nova/core';
import routeHelpers from '@satellite/helpers/routes';
import {
  completeMcpPortalLogin,
  readMcpTransaction,
  withMcpTransactionQuery
} from '@satellite/helpers/mcp-login-bridge';
import { useStore } from 'vuex';
import config from '@/config/config';

/**
 * Only allow same-origin relative paths (reject protocol-relative // and absolute URLs).
 * @param {unknown} redirectPath
 * @returns {string|null}
 */
export function sanitizeRedirectPath(redirectPath) {
  if (typeof redirectPath !== 'string' || redirectPath.length === 0) {
    return null;
  }
  if (!redirectPath.startsWith('/') || redirectPath.startsWith('//')) {
    return null;
  }
  return redirectPath;
}

/**
 * Mirror LoginForm watcher destination once the user is known to be authenticated.
 * @param {{ redirectPath?: unknown, me: object, defaultLoginPath?: string }} params
 * @returns {{ path: string } | { name: string }}
 */
export function resolveAuthenticatedLoginDestination({
  redirectPath,
  me,
  defaultLoginPath = 'appointments'
}) {
  const safePath = sanitizeRedirectPath(redirectPath);
  if (safePath && safePath !== '/login' && safePath !== '/') {
    return { path: safePath };
  }

  if (isInternalUser(me)) {
    return { name: 'internalHome' };
  }

  return { name: defaultLoginPath };
}

/**
 * With mcp_txn present, complete MCP from Helios for carriers and warehouse users.
 * Yard drivers still go to Circe (no MCP bridge there).
 * @param {{ me: object, mcpTransaction: string | null }} params
 * @returns {Promise<boolean>}
 */
export async function tryCompleteOrHopMcpLogin({ me, mcpTransaction }) {
  if (!mcpTransaction || !me) {
    return false;
  }

  if (isYardDriverUser(me)) {
    return routeHelpers.redirectYardDriverToCirce({
      user: me,
      circeUrl: config.circe_url
    });
  }

  return completeMcpPortalLogin({
    mcpTransaction,
    mcpUrl: config.mcp_url
  });
}

/**
 * Skip the login form when a valid session already exists.
 * @param {import('vue-router').RouteLocationNormalized} to
 * @param {import('vue-router').RouteLocationNormalized} _from
 * @param {import('vue-router').NavigationGuardNext} next
 */
export async function loginBeforeEnter(to, _from, next) {
  // Email verification lands on /login/:verificationToken and must reach LoginForm
  // so Auth/verifyEmail can run — even if a leftover session is still authorized.
  if (to.params?.verificationToken) {
    next();
    return;
  }

  const store = useStore();
  const mcpTransaction = readMcpTransaction(to.query);

  if (await routeHelpers.isUserAuthorized(store)) {
    const me = store.getters['Auth/me'];
    if (await tryCompleteOrHopMcpLogin({ me, mcpTransaction })) {
      return;
    }

    next(
      withMcpTransactionQuery(
        resolveAuthenticatedLoginDestination({
          redirectPath: to.query.redirectPath,
          me
        }),
        mcpTransaction
      )
    );
    return;
  }

  next();
}
